import React, {
  FunctionComponent,
  Suspense,
  useEffect,
  useState,
  useRef,
} from "react";
import { Link, useHistory } from "react-router-dom";
import logo from "assets/images/moderate-politics-logo.png";
import subscriptionIcon from "assets/svg/superhost-subscription.svg";
import groupChatGrid from "assets/svg/group-chat-grid.svg";
import dashboardIcon from "assets/svg/dashboard.svg";
import userIcon from "assets/svg/user_blue.svg";
import settingIcon from "assets/svg/settings_1.svg";
import logoutIcon from "assets/svg/logout.svg";
import helpIcon from "assets/svg/chat-info.svg";
import superHostKey from "assets/svg/superhost-user-key-indicator-for-avatar.svg";
import Button from "components/button/Button";
import Styles from "components/header/Header.module.scss";
import ModalShell from "components/chatModal/ChatModalShell.module.scss";
import CustomModal from "components/customModal/CustomModal";
import InputField from "components/textField/InputField";
import TextErrorMessage from "components/utils/errorMessage/TextErrorMessage";
import { getMediaUrl } from "store/services/URL";
import ProfileAvatar from "components/profileAvatar/ProfileAvatar";
import { callLogout } from "store/actions/auth.actions";
import { createRoom as createRoomActions } from "store/actions/conversation.actions";
import { useDispatch } from "react-redux";
import { getCurrentDate } from "helpers/utils";
import { trackEvent, ClarityEvents, trackNavigation, trackModalInteraction } from "utils/clarity";
import { ref as dbRef, onValue } from "firebase/database";
import { db, storage } from "utils/firebase-config";
import {
  ref,
  getDownloadURL,
  uploadBytesResumable,
  deleteObject,
} from "firebase/storage";
import { CardList, Subscription } from "modules/types/types";
import getItemFromLocalStorage from "utils/getItemFromLocalStorage";
import VideoPlayer from "components/videoPlayer/VideoPlayer";
import closeIconInModal from "assets/svg/close-icon-in-modal.svg";
import { featureFlags } from "config/featureFlags";
import { validateIntroVideoSize } from "helpers/utils";
const Stripe = React.lazy(() => import("views/Stripe/Stripe"));

/** Keep in sync with backend CreateRoom Cerberus maxlength and Room.topic max_length */
const TOPIC_MAX = 28;

type TopicError = null | "empty" | "too_long";

interface HeaderProps {
  getSubscriptionRequest: Function;
  createRoomRequest: Function;
  showPopup?: boolean;
  isLoading: boolean;
  status: boolean;
  createdRoom: any;
  subscriptions: Subscription;
  card: CardList;
  callStatus?: Function;
  /** Hide logo/actions chrome on phone; modals stay available. */
  hideOnPhone?: boolean;
}
const Header: FunctionComponent<HeaderProps> = (props: HeaderProps) => {
  const {
    getSubscriptionRequest,
    createRoomRequest,
    isLoading,
    showPopup,
    createdRoom,
    subscriptions,
    card,
    callStatus,
    status: isRoomCreated,
    hideOnPhone = false,
  } = props;
  const history = useHistory();
  const [topic, setTopic] = useState("");
  const [showDropDown, setShowDropdown] = useState(false);
  const [createRoom, setCreateRoom] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdRoomTitle, setCreatedRoomTitle] = useState("");
  const processedRoomRef = useRef(null);
  const isMountedRef = useRef(true);
  const subscriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [topicError, setTopicError] = useState<TopicError>(null);
  const [paymentPopUp, setPaymentPopUp] = useState(false);
  const [moveCallScreen, setMoveCallScreen] = useState<boolean>(false);
  const [conversations, setConversations] = useState<any>([]);
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [introVideo, setIntroVideo] = useState<File | null>(null);
  const [introVideoPreview, setIntroVideoPreview] = useState<string | null>(
    null
  );
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string | null>(null);
  const uploadTaskRef = useRef<any>(null);
  const uploadCancelledRef = useRef<boolean>(false);
  const tempVideoRefRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const dispatch = useDispatch();

  const handleChange = (value: string) => {
    setTopic(value);
    if (value.length > TOPIC_MAX) {
      setTopicError("too_long");
    } else {
      setTopicError(null);
    }
  };

  const handleRoomCreationSuccess = (room: any) => {
    if (
      room &&
      room.hasOwnProperty("pk") &&
      room.pk !== processedRoomRef.current
    ) {
      setCreateRoom(false);
      setCreatedRoomTitle(room.topic || "");
      setShowSuccessModal(true);
      document.body.classList.add("modal-open");
      trackModalInteraction("room_created_success", "opened");
      processedRoomRef.current = room.pk;

      // Reset the isRoomCreated flag to prevent re-triggering
      dispatch(createRoomActions.pending);
    }
  };

  const handleVideoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.split(".").pop() || "";

      // Check if it's a video file by extension or MIME type
      const videoExtensions = [
        "mp4",
        "webm",
        "ogg",
        "mov",
        "avi",
        "mkv",
        "flv",
        "wmv",
        "m4v",
        "3gp",
        "mpg",
        "mpeg",
      ];
      const isVideoByExtension = videoExtensions.includes(fileExtension);
      const isVideoByMimeType =
        file.type.startsWith("video/") ||
        file.type === "video/quicktime" ||
        file.type === "video/x-msvideo";

      if (isVideoByExtension || isVideoByMimeType || !file.type) {
        // Validate video file (type and size) using utility function
        const validationError = validateIntroVideoSize(file);
        if (validationError) {
          alert(validationError);
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          return;
        }

        // If no MIME type detected but has video extension, allow it
        // Cancel any ongoing upload
        if (uploadTaskRef.current) {
          try {
            uploadCancelledRef.current = true;
            uploadTaskRef.current.cancel();
          } catch (error) {
            console.error("Error cancelling previous upload:", error);
          }
        }
        // Reset previous upload state
        setUploadedVideoUrl(null);
        setUploadProgress(0);
        uploadCancelledRef.current = false;
        setIntroVideo(file);
        setIntroVideoPreview(URL.createObjectURL(file));
      } else {
        alert("Please select a video file");
      }
    }
  };

  // Auto-upload video when a file is selected
  useEffect(() => {
    if (introVideo && !uploadedVideoUrl && !isUploadingVideo && user?.pk) {
      const uploadVideo = async () => {
        setIsUploadingVideo(true);
        setUploadProgress(0);
        uploadCancelledRef.current = false;
        trackEvent(ClarityEvents.VIDEO_UPLOAD_STARTED, {
          file_size: introVideo.size,
        });
        let tempVideoRef: any = null;

        try {
          const tempVideoId = `${user.pk}-${Date.now()}`;
          tempVideoRef = ref(storage, `intro-videos/${tempVideoId}`);
          tempVideoRefRef.current = tempVideoRef; // Store reference for cleanup

          const uploadTask = uploadBytesResumable(tempVideoRef, introVideo, {
            contentType: introVideo.type || "video/mp4",
            cacheControl: "public, max-age=31536000",
          });
          uploadTaskRef.current = uploadTask; // Store reference for cancellation

          // Wait for upload to complete using a Promise
          await new Promise<void>((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                // Check if cancelled
                if (uploadCancelledRef.current) {
                  return;
                }
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
              },
              (error) => {
                // Don't log error if it was cancelled
                if (!uploadCancelledRef.current) {
                  console.error("Upload error:", error);
                  reject(error);
                }
              },
              () => {
                // Don't resolve if cancelled
                if (uploadCancelledRef.current) {
                  return;
                }
                setUploadProgress(100);
                uploadTaskRef.current = null;
                resolve();
              }
            );
          });

          // Check if cancelled before getting URL
          if (uploadCancelledRef.current) {
            return;
          }

          const videoUrl = await getDownloadURL(tempVideoRef);
          setUploadedVideoUrl(videoUrl);
          trackEvent(ClarityEvents.VIDEO_UPLOAD_COMPLETED, {
            file_size: introVideo.size,
          });
        } catch (error: any) {
          // Only show error if not cancelled
          if (!uploadCancelledRef.current) {
            console.error("Failed to upload video:", error);
            alert("Failed to upload video. Please try again.");
            // Clear video on error
            setIntroVideo(null);
            if (introVideoPreview) {
              URL.revokeObjectURL(introVideoPreview);
            }
            setIntroVideoPreview(null);
          }
        } finally {
          setIsUploadingVideo(false);
          uploadTaskRef.current = null;
          tempVideoRefRef.current = null;
        }
      };

      uploadVideo();
    }
  }, [introVideo, uploadedVideoUrl, isUploadingVideo, introVideoPreview, user]);

  const handleNext = () => {
    if (isLoading || isUploadingVideo) {
      return;
    }
    if (!topic.length) {
      setTopicError("empty");
      return;
    }
    if (topic.length > TOPIC_MAX) {
      setTopicError("too_long");
      return;
    }
    if (!user) {
      return;
    }
    setTopicError(null);
    createRoomRequest({
      topic,
      intro_video_url: uploadedVideoUrl || undefined,
    });
    trackEvent(ClarityEvents.ROOM_CREATED, {
      has_intro_video: !!uploadedVideoUrl,
    });
  };

  useEffect(() => {
    if (isRoomCreated && createdRoom.hasOwnProperty("pk")) {
      handleRoomCreationSuccess(createdRoom);
    }
  }, [isRoomCreated, createdRoom]);

  // Clear video state after room is created
  useEffect(() => {
    if (isRoomCreated && createdRoom?.pk) {
      // Video was already uploaded before room creation, so clear state
      setIntroVideo(null);
      if (introVideoPreview) {
        URL.revokeObjectURL(introVideoPreview);
      }
      setIntroVideoPreview(null);
      setUploadProgress(0);
      setUploadedVideoUrl(null);
      uploadTaskRef.current = null;
      tempVideoRefRef.current = null;
      uploadCancelledRef.current = false;
    }
  }, [isRoomCreated, createdRoom?.pk, introVideoPreview]);

  useEffect(() => {
    // Fetch subscription data when component mounts
    getSubscriptionRequest({});
  }, [getSubscriptionRequest]);

  // Cleanup effect to handle component unmounting
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (subscriptionTimeoutRef.current) {
        clearTimeout(subscriptionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const roomsRef = dbRef(db, `rooms`);

    const unsubscribe = onValue(roomsRef, (querySnapshot: any) => {
      const rooms: any = [];
      querySnapshot.forEach((doc: any) => {
        rooms.push(doc.val());
      });
      setConversations(rooms);
    });

    // Cleanup function to unsubscribe when component unmounts
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (user?.is_super_host && moveCallScreen && conversations.length > 0) {
      history.push({
        pathname: "/dashboard",
        state: { conversations: conversations },
      });
      // history.push({ pathname: '/call', state: { selectedBroadCast: createdRoom}});
    }
  }, [moveCallScreen, user, conversations, history]);

  useEffect(() => {
    if (showPopup) {
      setCreateRoom(true);
    }
  }, [showPopup]);

  const handleCreateRoom = () => (
      <>
        <div className={Styles.createRoomBody}>
          <p className={Styles.createRoomLead}>
            Enter your topic for this room
          </p>
          <p className={Styles.createRoomHint}>
            Character limit:{" "}
            <strong className={Styles.createRoomHintStrong}>{TOPIC_MAX}</strong>
          </p>
          <InputField
            placeholder="Topic..."
            type="text"
            value={topic}
            handleChange={(e: any) => handleChange(e)}
            isDisable={isLoading}
          />

          {featureFlags.showCreateRoomIntroVideo && (
            <div className={Styles.createRoomVideoSection}>
              <label className={Styles.createRoomVideoLabel}>
                Intro Video (Optional)
              </label>
              <div className="position-relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleVideoSelect}
                  className="form-control"
                  disabled={isUploadingVideo || isLoading}
                />
                {(introVideo || isUploadingVideo) && (
                  <div
                    className={Styles.videoClearIcon}
                    onClick={async () => {
                      if (isLoading) return;
                      // Cancel upload if in progress
                      if (uploadTaskRef.current) {
                        try {
                          uploadCancelledRef.current = true;
                          uploadTaskRef.current.cancel();

                          // Delete partially uploaded file from Firebase Storage
                          if (tempVideoRefRef.current) {
                            try {
                              await deleteObject(tempVideoRefRef.current);
                            } catch (deleteError: any) {
                              // Ignore errors if file doesn't exist or already deleted
                              if (
                                deleteError?.code !== "storage/object-not-found"
                              ) {
                                console.warn(
                                  "Error deleting partial upload:",
                                  deleteError
                                );
                              }
                            }
                          }
                        } catch (error) {
                          console.error("Error cancelling upload:", error);
                        }
                        uploadTaskRef.current = null;
                        tempVideoRefRef.current = null;
                      }
                      setIntroVideo(null);
                      if (introVideoPreview) {
                        URL.revokeObjectURL(introVideoPreview);
                      }
                      setIntroVideoPreview(null);
                      setIsUploadingVideo(false);
                      setUploadProgress(0);
                      setUploadedVideoUrl(null);
                      uploadCancelledRef.current = false;

                      // Reset file input
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        (e.currentTarget as HTMLElement).click();
                      }
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </div>
                )}
              </div>
              {introVideoPreview && (
                <div className="mt-2">
                  <VideoPlayer url={introVideoPreview} />
                </div>
              )}
              {isUploadingVideo && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                  <span className="fontThirteen text-blue">
                    Uploading video... {uploadProgress.toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          )}

          {topicError === "empty" && (
            <TextErrorMessage
              cssClass="text-center"
              message="Enter a topic to continue."
            />
          )}
          {topicError === "too_long" && (
            <TextErrorMessage
              cssClass="text-center"
              message={`Topic must be ${TOPIC_MAX} characters or fewer.`}
            />
          )}
        </div>
        <div className={ModalShell.footer}>
          <div className={Styles.createRoomFooterActions}>
            <button
              type="button"
              className={ModalShell.closeBtn}
              onClick={closeDialog}
              disabled={isLoading}
            >
              Close
              <img src={closeIconInModal} alt="" className={ModalShell.closeIcon} />
            </button>
            <Button
              label="Next"
              cssClass={`${Styles.createRoomNextBtn} ${topicError ? "errorButton" : ""}`}
              showLoading={isLoading}
              clickHandler={handleNext}
              icon
              disabled={isUploadingVideo || isLoading}
            />
          </div>
        </div>
      </>
  );

  // Take the host to the Super Host contact form (Settings › Super Host) instead
  // of opening the Stripe subscription flow. The room has already been created
  // at this point, so this only affects what happens after room creation.
  const handleContactSuperHost = () => {
    trackModalInteraction("super_host_subscription", "closed");
    document.body.classList.remove("modal-open");
    setCreateRoom(false);
    setSubscribed(false);
    setPaymentPopUp(false);
    history.push("/subscription");
  };

  // KEPT INTENTIONALLY (not deleted): the old Stripe subscription path. The
  // create-room modal now routes to the Super Host contact form via
  // handleContactSuperHost, so this handler is currently unused, but it (and the
  // stripePaymentModal/paymentDone helpers + paymentPopUp modal below) are
  // preserved in case the direct Stripe subscribe flow is reinstated.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSubscribeSuperHost = () => {
    setSubscribed(false);
    setPaymentPopUp(true);
    trackModalInteraction("super_host_subscription", "closed");
    trackModalInteraction("stripe_payment", "opened");
  };

  const stripePaymentModal = () => {
    document.body.classList.add("modal-open");
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Stripe
          type="subscribe"
          planId=""
          card={card}
          subscription={subscriptions}
          closeDialog={paymentDone}
        />
      </Suspense>
    );
  };

  const handleSubscribed = () => (
    <>
      <div className={Styles.createRoomBody}>
        <img
          src={subscriptionIcon}
          alt="Super Host"
          className={Styles.superHostImage}
        />
        <h4 className={Styles.superHostModalHeading}>
          Contact Us to be a Super Host!
        </h4>
        <p className={Styles.superHostModalCopy}>
          To join a room with more than 3 people will cost your guest a gold
          piece. Super Host lets your guests join rooms of any size for free.
          Contact us to get started and we&apos;ll help you become a Super Host.
        </p>
      </div>
      <div className={ModalShell.footer}>
        <div className={Styles.createRoomFooterActions}>
          <button
            type="button"
            className={ModalShell.closeBtn}
            onClick={handleSkip}
          >
            Skip
            <img src={closeIconInModal} alt="" className={ModalShell.closeIcon} />
          </button>
          <Button
            label="Contact Us"
            cssClass={Styles.createRoomNextBtn}
            clickHandler={handleContactSuperHost}
          />
        </div>
      </div>
    </>
  );

  const handleRoomCreatedSuccess = () => (
    <>
      <div className={Styles.createRoomBody}>
        <h4 className={Styles.successModalHeading}>
          Your room &ldquo;{createdRoomTitle}&rdquo; has been created successfully!
        </h4>
        <p className={Styles.successModalCopy}>
          You&apos;re the host. Remember to accept join requests, post regularly and
          invite your friends all from the room control panel.
        </p>
        <img
          src={groupChatGrid}
          alt="Group chat"
          className={Styles.hostInfographic}
        />
      </div>
      <div className={ModalShell.footer}>
        <button
          type="button"
          className={ModalShell.closeBtn}
          onClick={closeSuccessModal}
        >
          Okay
          <img src={closeIconInModal} alt="" className={ModalShell.closeIcon} />
        </button>
      </div>
    </>
  );

  const paymentDone = () => {
    trackModalInteraction("stripe_payment", "closed");
    document.body.classList.remove("modal-open");
    setCreateRoom(false);
    setSubscribed(false);
    setPaymentPopUp(false);
    history.push({
      pathname: "/dashboard",
      state: { conversations: conversations, selected: createdRoom, preserveSelection: true },
    });
  };

  const handleSubscriptionClose = () => {
    trackModalInteraction("super_host_subscription", "closed");
    setSubscribed(false);
    history.push({
      pathname: "/dashboard",
      state: { conversations: conversations, selected: createdRoom, preserveSelection: true },
    });
  };

  const handleSkip = () => {
    trackModalInteraction("super_host_subscription", "closed");
    document.body.classList.remove("modal-open");
    setCreateRoom(false);
    setSubscribed(false);
    setPaymentPopUp(false);
    history.push({
      pathname: "/dashboard",
      state: { conversations: conversations, selected: createdRoom, preserveSelection: true },
    });
  };

  const closeSuccessModal = () => {
    trackModalInteraction("room_created_success", "closed");
    setShowSuccessModal(false);
    setCreatedRoomTitle("");

    // Show subscription modal if user is not a super host, otherwise navigate directly
    if (user && !user.is_super_host) {
      setSubscribed(true);
      trackModalInteraction("super_host_subscription", "opened");
    } else {
      document.body.classList.remove("modal-open");
      history.push({
        pathname: "/dashboard",
        state: { conversations: conversations, selected: createdRoom, preserveSelection: true },
      });
    }
  };

  const closeDialog = async () => {
    if (createRoom && isLoading) {
      return;
    }

    // Cancel video upload if in progress
    if (uploadTaskRef.current) {
      try {
        uploadCancelledRef.current = true;
        uploadTaskRef.current.cancel();

        // Delete partially uploaded file from Firebase Storage
        if (tempVideoRefRef.current) {
          try {
            await deleteObject(tempVideoRefRef.current);
          } catch (deleteError: any) {
            // Ignore errors if file doesn't exist or already deleted
            if (deleteError?.code !== "storage/object-not-found") {
              console.warn("Error deleting partial upload:", deleteError);
            }
          }
        }
      } catch (error) {
        console.error("Error cancelling upload:", error);
      }
      uploadTaskRef.current = null;
      tempVideoRefRef.current = null;
    }

    // Clear video state
    setIntroVideo(null);
    if (introVideoPreview) {
      URL.revokeObjectURL(introVideoPreview);
    }
    setIntroVideoPreview(null);
    setIsUploadingVideo(false);
    setUploadProgress(0);
    setUploadedVideoUrl(null);
    uploadCancelledRef.current = false;

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (createRoom) trackModalInteraction("create_room", "closed");
    if (subscribed) trackModalInteraction("super_host_subscription", "closed");
    if (paymentPopUp) trackModalInteraction("stripe_payment", "closed");

    document.body.classList.remove("modal-open");
    setCreateRoom(false);
    setSubscribed(false);
    setPaymentPopUp(false);
    setTopicError(null);
  };

  const handleDropDown = () => {
    setShowDropdown(!showDropDown);
  };

  const handleBgDropDown = () => {
    setShowDropdown(!showDropDown);
  };

  const showPublicProfile = () => {
    trackNavigation("/profile", "header_dropdown");
    history.push("/profile");
  };

  const handleLogOut = () => {
    dispatch(callLogout());
    history.push("/login");
  };

  useEffect(() => {}, [card]);

  useEffect(() => {
    getSubscriptionRequest({});
  }, []);

  // Enhanced authentication handling
  useEffect(() => {
    const checkAuth = () => {
      const userData = getItemFromLocalStorage("user");
      if (userData && userData.access && userData.pk && userData.name) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        // Redirect to login if no valid user
        history.push("/login");
      }
    };

    checkAuth();

    // Listen for token expiration events
    const handleTokenExpired = () => {
      console.log("Token expired event received in Header");
      setUser(null);
      setIsAuthenticated(false);
      history.push("/login");
    };

    const handleAuthRequired = () => {
      console.log("Auth required event received in Header");
      setUser(null);
      setIsAuthenticated(false);
      history.push("/login");
    };

    window.addEventListener("tokenExpired", handleTokenExpired);
    window.addEventListener("authRequired", handleAuthRequired);

    return () => {
      window.removeEventListener("tokenExpired", handleTokenExpired);
      window.removeEventListener("authRequired", handleAuthRequired);
    };
  }, [history]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    const SCROLL_RANGE = 80;

    const updateProgress = () => {
      scrollRafRef.current = null;
      const progress = Math.min(window.scrollY / SCROLL_RANGE, 1);
      headerRef.current?.style.setProperty(
        "--header-progress",
        String(progress)
      );
    };

    const onScroll = () => {
      if (scrollRafRef.current === null) {
        scrollRafRef.current = requestAnimationFrame(updateProgress);
      }
    };

    updateProgress();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
      }
      headerRef.current?.style.removeProperty("--header-progress");
    };
  }, [isAuthenticated]);

  // Don't render if not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  const handleLogoClick = () => {
    trackNavigation("/dashboard", "logo");
    history.push("/dashboard");
  };

  return (
    <header
      ref={headerRef}
      className={`${Styles.siteHeader} ${hideOnPhone ? Styles.hideOnPhone : ''}`}
    >
      <div className="custom-container h-100">
        <div className={Styles.headerWrapper}>
          <div
            className={`d-inline-block ${Styles.logoWrapper}`}
            onClick={handleLogoClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleLogoClick();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Go to dashboard"
          >
            <img src={logo} alt="Moderate Politics" className={Styles.logo} />
          </div>
          <div className={Styles.profileName}>
            <Button
              label="CREATE ROOM"
              clickHandler={() => {
                setCreateRoom(true);
                trackModalInteraction("create_room", "opened");
              }}
              cssClass={Styles.createRoomWidth}
              iconClass="fas fa-plus"
              icon
            />
            <span className={Styles.divider} />
            <div
              className="d-flex column-gap-20 align-items-center position-relative"
              onClick={handleDropDown}
              role="button"
              onKeyPress={handleDropDown}
              tabIndex={0}
            >
              <div className={`d-inline-block ${Styles.profileLogoWrapper}`}>
                <ProfileAvatar
                  profile={user?.profile}
                  alt="profile"
                  className={Styles.profileIcon}
                />
                {user?.is_super_host && (
                  <img src={superHostKey} alt="" className={Styles.superHostKey} />
                )}
              </div>
              <p
                className={`mb-0 fontSixteen text-blue font-weight-bolder text-capitalize ${Styles.userNameWrapper}`}
              >
                <span>{user?.name || "User"}</span>
                <i className="fas fa-angle-down ml-2" />
              </p>
              {showDropDown && (
                <>
                  <div className={Styles.dropDownWrapper}>
                    <ul className={`${Styles.dropDownInner} list-unstyled`}>
                      <li>
                        <Link
                          className="text-decoration-none text-black fontSixteen font-weight-normal"
                          to="/dashboard"
                          onClick={() => trackNavigation("/dashboard", "header_dropdown")}
                        >
                          <img
                            className="pr-2"
                            src={dashboardIcon}
                            alt="dashboard"
                          />
                          Dashboard
                        </Link>
                      </li>
                      <li>
                        <div
                          className="d-flex"
                          onClick={showPublicProfile}
                          onKeyPress={showPublicProfile}
                          role="button"
                          tabIndex={0}
                        >
                          <img className="pr-2" src={userIcon} alt="user" />
                          <p className="text-decoration-none mb-0 text-black fontSixteen font-weight-normal">
                            View Public Profile
                          </p>
                        </div>
                      </li>
                      <li>
                        <Link
                          className="text-decoration-none text-black fontSixteen font-weight-normal"
                          to="/profile/edit"
                          onClick={() => trackNavigation("/profile/edit", "header_dropdown")}
                        >
                          <img
                            className="pr-2"
                            src={settingIcon}
                            alt="settings"
                          />
                          Settings
                        </Link>
                      </li>
                      <li>
                        <Link
                          className="text-decoration-none text-black fontSixteen font-weight-normal"
                          to="/help"
                          onClick={() => trackNavigation("/help", "header_dropdown")}
                        >
                          <img
                            className="pr-2"
                            src={helpIcon}
                            alt="help"
                            style={{
                              width: "30px",
                              height: "30px",
                              marginLeft: "-3px",
                            }}
                          />
                          Help
                        </Link>
                      </li>
                      <li>
                        <div
                          onClick={handleLogOut}
                          onKeyPress={handleLogOut}
                          role="button"
                          tabIndex={0}
                        >
                          <img className="pr-2" src={logoutIcon} alt="logout" />
                          Logout
                        </div>
                      </li>
                    </ul>
                  </div>
                  <div
                    className={Styles.bgDropDown}
                    onClick={handleBgDropDown}
                    role="button"
                    onKeyPress={handleBgDropDown}
                    tabIndex={0}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {createRoom && (
        <CustomModal
          title="Create Room"
          wrapperClass={ModalShell.wrapper}
          modalHeaderClass={ModalShell.header}
          modalTitleClass={Styles.createRoomModalTitle}
          cssClass={ModalShell.body}
          closeDialog={closeDialog}
          closeModal={closeDialog}
          close={false}
        >
          {handleCreateRoom()}
        </CustomModal>
      )}
      {showSuccessModal && (
        <CustomModal
          title="Room Created"
          wrapperClass={ModalShell.wrapper}
          modalHeaderClass={ModalShell.header}
          modalTitleClass={ModalShell.title}
          cssClass={ModalShell.body}
          closeDialog={closeSuccessModal}
          closeModal={closeSuccessModal}
          close={false}
        >
          {handleRoomCreatedSuccess()}
        </CustomModal>
      )}
      {subscribed && (
        <CustomModal
          wrapperClass={ModalShell.wrapper}
          cssClass={ModalShell.body}
          closeDialog={handleSubscriptionClose}
          closeModal={handleSubscriptionClose}
          close={false}
          hideHeader
        >
          {handleSubscribed()}
        </CustomModal>
      )}
      {paymentPopUp && (
        <CustomModal
          closeDialog={closeDialog}
          title="Subscribe"
          wrapperClass={Styles.rechargeAccountWrapper}
        >
          {stripePaymentModal()}
        </CustomModal>
      )}
    </header>
  );
};

export default Header;
